import React, { useState, useCallback, useMemo, useRef } from 'react'
import { Grid } from '@material-ui/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useGetQueryBlueprint, useGetQueryBlueprintDefinition, useListQueryBlueprintVersions } from '../../../../../api/denali'
import Loading from '../../../../molecules/Loading'
import AdminPage from '../../../admin/shared/AdminPage'
import ErrorDisplay from '../../../../molecules/ErrorDisplay'
import SydTabs from '../../../../commonDesign/SydTabs'
import FlexRow from '../../../../molecules/FlexRow'
import SydButton from '../../../../commonDesign/Button'
import openHelp from '../../../../../apps/helpApp/openHelp'
import GeneralTab from './components/GeneralTab'
import VersionHistoryTab from './components/VersionHistoryTab'
import TestExecutionTab from './components/TestExecutionTab'
import NewVersionDialog from './components/NewVersionDialog'
import SetActiveVersionDialog from './components/SetActiveVersionDialog'
import DeleteVersionDialog from './components/DeleteVersionDialog'

const tabs = {
  general: {
    label: 'General',
    value: 'general'
  },
  history: {
    label: 'Version History',
    value: 'history'
  },
  test: {
    label: 'Test',
    value: 'test'
  }
}

function QueryBlueprintDetails ({ root }) {
  const { blueprintId } = useParams()
  const [currentTab, setCurrentTab] = useState('general')
  const [isEditing, setIsEditing] = useState(false)
  const addDefinitionRef = useRef(null)
  const setActiveVersionRef = useRef(null)
  const deleteVersionRef = useRef(null)

  // Independent version selection per tab
  const [generalVersion, setGeneralVersion] = useState(null) // null = use active
  const [testVersion, setTestVersion] = useState(null) // null = use active

  const { data: blueprint, isLoading, error, refetch } = useGetQueryBlueprint(
    parseInt(blueprintId),
    { includes: { definition: true } }
  )

  const { data: versions, refetch: refetchVersions } = useListQueryBlueprintVersions(
    parseInt(blueprintId)
  )

  // Fetch specific version data when selected
  const { data: generalVersionData } = useGetQueryBlueprintDefinition(
    generalVersion?.definitionId,
    { enabled: !!generalVersion }
  )

  const { data: testVersionData } = useGetQueryBlueprintDefinition(
    testVersion?.definitionId,
    { enabled: !!testVersion }
  )

  // Wrap definition data in blueprint structure for consistency
  const generalDisplayData = useMemo(() => {
    if (!generalVersion || !generalVersionData) return blueprint
    return {
      ...blueprint,
      definition: generalVersionData
    }
  }, [generalVersion, generalVersionData, blueprint])

  const testDisplayData = useMemo(() => {
    if (!testVersion || !testVersionData) return blueprint
    return {
      ...blueprint,
      definition: testVersionData
    }
  }, [testVersion, testVersionData, blueprint])

  const handleAddDefinition = useCallback(() => {
    addDefinitionRef.current.open()
  }, [])

  const handleSetActiveVersion = useCallback((version) => {
    setActiveVersionRef.current.open(version)
  }, [])

  const handleDeleteVersion = useCallback((version) => {
    deleteVersionRef.current.open(version)
  }, [])

  const handleDefinitionComplete = useCallback(() => {
    refetch()
    refetchVersions()
  }, [refetch, refetchVersions])

  const handleDeleteComplete = useCallback(() => {
    refetchVersions()
    refetch()
    // Reset to active if deleted version was selected
    if (generalVersion && versions) {
      const stillExists = versions.some(v => v.definitionId === generalVersion.definitionId)
      if (!stillExists) {
        setGeneralVersion(null)
      }
    }
    if (testVersion && versions) {
      const stillExists = versions.some(v => v.definitionId === testVersion.definitionId)
      if (!stillExists) {
        setTestVersion(null)
      }
    }
  }, [refetchVersions, refetch, generalVersion, testVersion, versions])

  if (error) {
    return (
      <AdminPage>
        <ErrorDisplay error={error} onRetry={refetch} />
      </AdminPage>
    )
  }

  if (isLoading || !blueprint) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <SydTabs.Group
        tabOptions={tabs}
        onChange={setCurrentTab}
        defaultValue={currentTab}
        key='query_blueprint_details_tabs'
      >
        <Grid container spacing={2}>
          {/* Page Header with Tabs */}
          <Grid item xs={12}>
            <FlexRow className='justify-between' style={{ alignItems: 'center', marginBottom: '16px' }}>
              <h1>[Blueprint] {blueprint.name}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SydTabs.MiniList />
                <SydButton
                  variant='ghost'
                  size='sm'
                  onClick={() => openHelp('query-blueprints')}
                >
                  <HelpOutlineIcon fontSize='small' />
                  <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>Help</span>
                </SydButton>
              </div>
            </FlexRow>
          </Grid>

          {/* Tab Content */}
          <Grid item xs={12}>
            <SydTabs.Tab value='general'>
              <GeneralTab
                blueprint={blueprint}
                selectedVersion={generalVersion}
                onSelectVersion={setGeneralVersion}
                versionData={generalDisplayData}
                allVersions={versions || []}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                onSave={refetch}
                onAddDefinition={handleAddDefinition}
                onSetActive={handleSetActiveVersion}
              />
            </SydTabs.Tab>

            <SydTabs.Tab value='history'>
              <VersionHistoryTab
                blueprint={blueprint}
                versions={versions || []}
                onSetActive={handleSetActiveVersion}
                onDeleteVersion={handleDeleteVersion}
              />
            </SydTabs.Tab>

            <SydTabs.Tab value='test'>
              <TestExecutionTab
                blueprint={blueprint}
                selectedVersion={testVersion}
                onSelectVersion={setTestVersion}
                versionData={testDisplayData}
                allVersions={versions || []}
              />
            </SydTabs.Tab>
          </Grid>
        </Grid>
      </SydTabs.Group>

      <NewVersionDialog
        ref={addDefinitionRef}
        blueprint={blueprint}
        allVersions={versions || []}
        onComplete={handleDefinitionComplete}
      />
      <SetActiveVersionDialog
        ref={setActiveVersionRef}
        blueprint={blueprint}
        onComplete={handleDefinitionComplete}
      />
      <DeleteVersionDialog
        ref={deleteVersionRef}
        blueprint={blueprint}
        onComplete={handleDeleteComplete}
      />
    </AdminPage>
  )
}

QueryBlueprintDetails.propTypes = {
  root: PropTypes.any
}

export default QueryBlueprintDetails
